import type { ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CollisionPriority } from '@dnd-kit/abstract';
import { useDroppable } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';

import { FloatingBlockToolbarPortal } from '@/components/editor/toolbar/floating-block-toolbar-portal';
import { AddElementButton } from '@/components/page-editor/page-section-toolbar';
import {
  AbsoluteBackgroundStack,
  resolveBackgroundConfig,
} from '@/components/shared/background-settings/absolute-stack';
import { Badge } from '@/components/ui/badge';
import { useEffectiveColors } from '@/hooks/use-public-design-settings';
import { useStickyHover } from '@/hooks/use-sticky-hover';
import type { CollectionCardConfig } from '@/lib/blocks/collection/blocks';
import {
  createEmptyCollectionItem,
  findCardTemplateBlock,
  getCardBgClassName,
  isCollectionBlock,
  resolveCardConfigFromTemplate,
  resolveCollectionLayout,
  resolveSymmetricCollectionProps,
} from '@/lib/blocks/collection/blocks';
import {
  type DataWidgetBlockType,
  filterDataWidgetFieldsForSourceMode,
  getDataWidgetLabel,
  getDataWidgetVariantProps,
  isDataWidgetBlock,
  isDataWidgetBlockType,
  resolveDataWidgetProps,
} from '@/lib/blocks/collection/data-widget/blocks';
import {
  getIndexCollectionVariantProps,
  isIndexCollectionBlock,
  resolveIndexCollectionProps,
} from '@/lib/blocks/collection/index-collection/blocks';
import { getVariantEntry } from '@/lib/blocks/collection/variant-registry';
import { getBlockDisplayWidthStyle } from '@/lib/blocks/layout/block-layout';
import {
  GROUP_MEDIA_SHELL_CLASSNAME,
  getGroupChildClassName,
  getGroupContainerClassName,
  getGroupCustomStyle,
  getGroupCustomStyleClassName,
  getGroupSurfaceClassName,
  hasGroupMediaBackground,
  isBlockFloated,
  resolveGroupLayout,
  shouldStretchGroupToFillParent,
  shouldUseBlockFlowForAncestorFloat,
} from '@/lib/blocks/layout/group-layout';
import { collectDescendantStableIds } from '@/lib/blocks/tree';
import { SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES } from '@/lib/derive-content-tone';
import { getMainSections } from '@/lib/page-utils';
import { cn } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import {
  type BlockStructuralRegion,
  getBlockUiKey,
  isParentBlockType,
  type PageBlock,
} from '@/types/block';
import { DataWidgetRenderer } from '../renderers/data-widget-renderer';
import { IndexCollectionRenderer } from '../renderers/index-collection-renderer';
import {
  useActiveBlockId,
  useBlockSelectionBindings,
  useBlockSelectionStore,
  useClearActiveForSection,
  useInlineEditBlockId,
  useIsBlockActive,
  useIsBlockHovered,
  useIsBlockToolbarRowVisible,
  useIsOverlayFocused,
} from './block-selection';
import { BlockShell } from './block-shell';
import { CollectionSettingsDialog } from './collection/settings-dialog';
import { SortableCollectionItems } from './collection/sortable-items';
import { DataWidgetSettingsDialog } from './data-widget-settings-dialog';
import { createDynamicCollisionDetector } from './dnd/collision/dynamic-detector';
import { type DropIndicator, type DropParentDirection, isDirectionalEdge } from './dnd/core';
import {
  DND_ACTIVE_SCOPE_CLASS,
  getParentDropSurfaceIndicatorClassName,
  getPlacementIndicatorClassName,
  getPreviewGapClassName,
} from './dnd/drag-visuals';
import { GapZone } from './dnd/drop-targets/gap-zone';
import type { EditorContentAlign } from './editor-content-bounds';
import { IndexCollectionSettingsDialog } from './index-collection-settings-dialog';
import { SortableLeafBlock } from './sortable-leaf-block';
import { BlockActionToolbar } from './toolbar/block-action-toolbar';
import { GroupSettingsPopover } from './toolbar/group-settings-popover';
import { resolveParentToolbarPolicy } from './toolbar/toolbar-policy';

const mergeRefs =
  (...refs: Array<((node: HTMLElement | null) => void) | null>) =>
  (node: HTMLElement | null) => {
    // Parent blocks need both sortable ref and nested parent-drop ref on the same node.
    refs.forEach((ref) => {
      ref?.(node);
    });
  };

const EMPTY_STATE_BASE = 'rounded-md border border-dashed border-border/60';

function GroupBackgroundShell({
  layout,
  blockProps,
  siteTextColor,
  stretch,
  children,
}: {
  layout: ReturnType<typeof resolveGroupLayout>;
  blockProps: Record<string, unknown>;
  siteTextColor?: string;
  stretch?: boolean;
  children: ReactNode;
}) {
  if (!hasGroupMediaBackground(layout)) {
    return <>{children}</>;
  }
  const backgroundConfig = resolveBackgroundConfig(blockProps, siteTextColor);
  if (!backgroundConfig) {
    return <>{children}</>;
  }
  return (
    <div
      className={cn(
        GROUP_MEDIA_SHELL_CLASSNAME,
        SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES[backgroundConfig.contentTone],
        stretch && 'h-full flex-1',
      )}
    >
      <AbsoluteBackgroundStack
        config={backgroundConfig}
        focusX={layout.backgroundFocusX}
        focusY={layout.backgroundFocusY}
        fixed={layout.backgroundFixed && backgroundConfig.mode === 'image'}
      />
      {children}
    </div>
  );
}

const isSingleColumnCollectionLayout = (layout: ReturnType<typeof resolveCollectionLayout>) =>
  layout.columns.mobile === 1 && layout.columns.tablet === 1 && layout.columns.desktop === 1;

interface SortableParentBlockProps {
  sectionId: string;
  editorSectionId: string;
  block: PageBlock;
  parentId: string | null;
  parentStableId: string | null;
  parentBlockType?: PageBlock['blockType'] | null;
  parentDirection: DropParentDirection;
  childrenByParent: Map<string, PageBlock[]>;
  dropIndicator: DropIndicator | null;
  onUpdateGroupProps: (blockId: string, patch: Record<string, unknown>) => void;
  isDragSessionActive?: boolean;
  allowedScopeParentId?: string | null;
  disabled?: boolean;
  dndDisabled?: boolean;
  stretchToParent?: boolean;
  layoutClassName?: string;
  hasActiveAncestor?: boolean;
  structuralEditableBlockIds?: ReadonlySet<string>;
  protectedBlockIds?: ReadonlySet<string>;
  structuralRegionByRootId?: ReadonlyMap<string, BlockStructuralRegion>;
  participatesInAncestorFloatFlow?: boolean;
}

interface StructuralRegionAddElementProps {
  sectionId: string;
  parentBlockId: string;
  structuralRegion: BlockStructuralRegion;
  hasOptionalElements: boolean;
  isBlockActive: boolean;
  isHovered: boolean;
  anchorRef: RefObject<HTMLDivElement | null>;
}

function StructuralRegionAddElement({
  sectionId,
  parentBlockId,
  structuralRegion,
  hasOptionalElements,
  isBlockActive,
  isHovered,
  anchorRef,
}: StructuralRegionAddElementProps) {
  const section = usePageEditorStore((state) =>
    getMainSections(state.page).find((candidate) => candidate.id === sectionId),
  );
  const [isOpen, setIsOpen] = useState(false);

  const isVisible = isOpen || (!isBlockActive && (!hasOptionalElements || isHovered));

  if (!section || !isVisible) return null;

  return (
    <FloatingBlockToolbarPortal anchorRef={anchorRef} placement="inside-top-start">
      <AddElementButton
        section={section}
        parentBlockId={parentBlockId}
        allowedBlockTypes={structuralRegion.allowedBlockTypes}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      />
    </FloatingBlockToolbarPortal>
  );
}

export function SortableParentBlock({
  sectionId,
  editorSectionId,
  block,
  parentId,
  parentStableId,
  parentBlockType = null,
  parentDirection,
  childrenByParent,
  dropIndicator,
  onUpdateGroupProps,
  isDragSessionActive = false,
  allowedScopeParentId,
  disabled,
  dndDisabled,
  stretchToParent = false,
  layoutClassName,
  hasActiveAncestor = false,
  structuralEditableBlockIds,
  protectedBlockIds,
  structuralRegionByRootId,
  participatesInAncestorFloatFlow = false,
}: SortableParentBlockProps) {
  const blockRootRef = useRef<HTMLDivElement | null>(null);
  const groupContainerRef = useRef<HTMLDivElement | null>(null);
  const collisionDetector = useMemo(() => createDynamicCollisionDetector(), []);
  const cardColors = useEffectiveColors();
  const deleteBlock = usePageEditorStore((state) => state.deleteBlock);
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const setActiveBlock = useBlockSelectionStore((state) => state.setActiveBlock);
  const blockUiKey = getBlockUiKey(block);
  const isOutsideStructuralRegion = Boolean(
    structuralEditableBlockIds && !structuralEditableBlockIds.has(blockUiKey),
  );
  const isProtectedBlock = protectedBlockIds?.has(blockUiKey) ?? false;
  const isParentProtected = Boolean(parentStableId && protectedBlockIds?.has(parentStableId));
  const structuralRegion = structuralRegionByRootId?.get(blockUiKey);
  const isDndDisabled = Boolean(
    disabled || dndDisabled || isOutsideStructuralRegion || isProtectedBlock,
  );
  const isDropDisabled = Boolean(disabled || dndDisabled || isOutsideStructuralRegion);
  // Parent block (currently group) is both draggable itself and a drop container for children.
  const { ref: sortableRef, isDragging } = useSortable({
    id: blockUiKey,
    data: { type: 'block', parentId: parentStableId, isParentBlock: true },
    disabled: isDndDisabled,
    index: block.position,
    collisionPriority: CollisionPriority.Low,
    collisionDetector,
  });
  const inlineEditBlockId = useInlineEditBlockId(editorSectionId);
  const descendantUiKeys = useMemo(
    () => collectDescendantStableIds(blockUiKey, childrenByParent),
    [blockUiKey, childrenByParent],
  );
  const isInlineEditInSubtree = useMemo(() => {
    if (!inlineEditBlockId) {
      return false;
    }

    return descendantUiKeys.has(inlineEditBlockId);
  }, [descendantUiKeys, inlineEditBlockId]);
  const isHoverInSubtree = useBlockSelectionStore((state) => {
    if (state.hovered?.sectionId !== editorSectionId) return false;
    const hoveredId = state.hovered.blockId;
    return hoveredId === blockUiKey || descendantUiKeys.has(hoveredId);
  });
  const {
    isHovered: stickyHoverInSubtree,
    cancelHide: cancelCollectionHoverHide,
    scheduleHide: scheduleCollectionHoverHide,
  } = useStickyHover(250);
  useEffect(() => {
    if (isHoverInSubtree) {
      cancelCollectionHoverHide();
    } else if (stickyHoverInSubtree) {
      scheduleCollectionHoverHide();
    }
  }, [
    isHoverInSubtree,
    stickyHoverInSubtree,
    cancelCollectionHoverHide,
    scheduleCollectionHoverHide,
  ]);
  const { ref: setDropRef } = useDroppable({
    id: `parent-drop-${blockUiKey}`,
    data: { type: 'parent-drop', parentId: blockUiKey },
    disabled: isDropDisabled || isInlineEditInSubtree,
    collisionPriority: CollisionPriority.Normal,
    collisionDetector,
  });

  const isCollectionParent = isCollectionBlock(block);
  const isDataWidgetParent = isDataWidgetBlock(block);
  const isIndexCollectionParent = isIndexCollectionBlock(block);
  const isCardBackedParent = isCollectionParent || isDataWidgetParent || isIndexCollectionParent;
  const [collectionItemReorderDragActive, setCollectionItemReorderDragActive] = useState(false);
  const cardTemplateBlock = isCardBackedParent
    ? (findCardTemplateBlock(childrenByParent.get(blockUiKey) || []) ?? undefined)
    : undefined;
  const cardTemplateChildren = cardTemplateBlock
    ? (childrenByParent.get(getBlockUiKey(cardTemplateBlock)) ?? [])
    : [];
  const cardConfig: CollectionCardConfig = useMemo(() => {
    if (!cardTemplateBlock) return { gap: 'md', align: 'start', fields: [] };
    if (isCollectionParent) {
      const symProps = resolveSymmetricCollectionProps(block);
      const variantEntry = getVariantEntry(symProps.variant);
      const variantSpecific = (symProps.variantProps[symProps.variant] ??
        variantEntry?.defaultProps ??
        {}) as Record<string, unknown>;
      return resolveCardConfigFromTemplate(
        cardTemplateBlock,
        cardTemplateChildren,
        variantSpecific,
      );
    }
    if (isIndexCollectionParent) {
      const props = resolveIndexCollectionProps(block);
      return resolveCardConfigFromTemplate(
        cardTemplateBlock,
        cardTemplateChildren,
        getIndexCollectionVariantProps(props),
      );
    }
    if (isDataWidgetParent) {
      const dataWidgetProps = resolveDataWidgetProps(block);
      const config = resolveCardConfigFromTemplate(
        cardTemplateBlock,
        cardTemplateChildren,
        getDataWidgetVariantProps(dataWidgetProps),
      );
      return {
        ...config,
        fields: filterDataWidgetFieldsForSourceMode(
          block.blockType as DataWidgetBlockType,
          config.fields,
          dataWidgetProps.source.mode,
        ),
      };
    }
    return { gap: 'md', align: 'start', fields: [] };
  }, [
    block,
    cardTemplateBlock,
    cardTemplateChildren,
    isCollectionParent,
    isDataWidgetParent,
    isIndexCollectionParent,
  ]);
  const isGroupBlock = block.blockType === 'group';
  const isParentLike = isCardBackedParent || isGroupBlock;
  const showFloatOption = parentBlockType === 'group';
  const activeBlockId = useActiveBlockId(editorSectionId);
  const isBlockActive = useIsBlockActive(editorSectionId, blockUiKey);
  const isBlockHovered = useIsBlockHovered(editorSectionId, blockUiKey);
  const isAllowedScope = isDragSessionActive && allowedScopeParentId === blockUiKey;
  const isDropHover =
    isParentLike &&
    (dropIndicator?.targetId === blockUiKey ||
      dropIndicator?.targetId === `parent-drop-${blockUiKey}`);
  const dropEdge = dropIndicator?.targetId === blockUiKey ? dropIndicator.edge : null;
  const previewGapClass =
    isDragSessionActive && isDirectionalEdge(dropEdge) ? getPreviewGapClassName(dropEdge) : null;
  const placementPreviewNode =
    isDragSessionActive && isDirectionalEdge(dropEdge) ? (
      <div className={getPlacementIndicatorClassName(dropEdge)} />
    ) : null;
  const isToolbarRowVisibleBySelection = useIsBlockToolbarRowVisible(editorSectionId, blockUiKey);
  const children = childrenByParent.get(blockUiKey) || [];
  const groupLayout = isGroupBlock ? resolveGroupLayout(block, children) : null;
  const collectionLayout = isCollectionParent ? resolveCollectionLayout(block) : null;
  const blockSelectionBindings = useBlockSelectionBindings(editorSectionId, blockUiKey);
  const clearActiveForSection = useClearActiveForSection(editorSectionId);
  const isSelectionScope = Boolean(
    activeBlockId && activeBlockId !== blockUiKey && descendantUiKeys.has(activeBlockId),
  );
  // Card-backed parents (collection, widget, index) handle descendant focus chrome via
  // SortableCollectionItems or collection context toolbar — not the parent shell ring.
  const showSelectionScope = isSelectionScope && !isCardBackedParent;
  const hideCollectionChromeForDeepEdit = useBlockSelectionStore((s) => {
    if (
      s.collectionRichTextEditorSectionId !== null &&
      s.collectionRichTextEditorSectionId === editorSectionId
    ) {
      return true;
    }
    return Boolean(
      s.suppressChromeForInlineText && inlineEditBlockId && descendantUiKeys.has(inlineEditBlockId),
    );
  });
  const dataWidgetProps = isDataWidgetParent ? resolveDataWidgetProps(block) : null;
  const isStaticDataWidget = isDataWidgetParent && dataWidgetProps?.source.mode === 'static';
  const isOverlayFocused = useIsOverlayFocused();
  const hoverChromeAllowed = !hasActiveAncestor && !isOverlayFocused;
  /** Collection chrome (add card, layout settings): shown when editing this block, inside its card template, or hovering anywhere inside the collection — not for every mounted collection. */
  const showCollectionContextToolbar = Boolean(
    isCardBackedParent &&
      (isSelectionScope ||
        isInlineEditInSubtree ||
        (hoverChromeAllowed && (isHoverInSubtree || stickyHoverInSubtree))) &&
      !hideCollectionChromeForDeepEdit,
  );
  const toolbarPolicy = resolveParentToolbarPolicy({
    parentStableId,
    parentBlockType,
    isGroupBlock,
    lockedSection: isOutsideStructuralRegion || isProtectedBlock,
  });
  const canSelectParent = toolbarPolicy.canSelectParent && !isParentProtected;
  const showGroupSettings = toolbarPolicy.canShowGroupSettings;
  const childParentDirection: DropParentDirection = isCollectionParent
    ? collectionLayout && isSingleColumnCollectionLayout(collectionLayout)
      ? 'vertical'
      : 'grid'
    : isCardBackedParent && !isCollectionParent
      ? 'vertical'
      : // Float-mode groups always render children as a single stacked block flow (`flow-root`),
        // regardless of the group's configured `direction` — real CSS float creates the visual
        // side-by-side effect, not flex/grid. Left/right edge zones only make sense for an actual
        // horizontal flex row, so drops into a floating group must use vertical (top/bottom) intent.
        groupLayout?.isFloatLayout
        ? 'vertical'
        : (groupLayout?.direction ?? 'vertical');
  const resolvedGroupLayout = groupLayout || resolveGroupLayout(block, children);
  const blockFlowForAncestorFloat = shouldUseBlockFlowForAncestorFloat(
    block,
    children,
    resolvedGroupLayout,
    participatesInAncestorFloatFlow,
  );
  const firstFloatedChildIndex = resolvedGroupLayout.isFloatLayout
    ? children.findIndex(isBlockFloated)
    : -1;
  const shouldStretchChildren =
    !isCollectionParent &&
    resolvedGroupLayout.direction === 'horizontal' &&
    resolvedGroupLayout.align === 'stretch';
  const shouldStretchRootGroup =
    parentId === null && shouldStretchGroupToFillParent(resolvedGroupLayout);
  const childContentAlign: EditorContentAlign =
    groupLayout?.direction === 'vertical' ? groupLayout.align : 'stretch';

  const getChildWrapperClassName = (child: PageBlock): string => {
    if (isCollectionParent || !groupLayout) {
      return 'min-w-0';
    }

    if (groupLayout.direction === 'vertical' && isParentBlockType(child.blockType)) {
      return cn(
        getGroupChildClassName(child, groupLayout, { blockFlowForAncestorFloat }),
        'w-full',
      );
    }

    return getGroupChildClassName(child, groupLayout, { blockFlowForAncestorFloat });
  };

  /**
   * The parent's inner surface carries the sortable ref (merged with droppable).
   * When a group/collection parent is the active selection, `parentOwnsDragInteraction`
   * disables nested sortables so dragging the surface moves the parent, not a child.
   */
  const toolbarRowVisible =
    !isDragging &&
    !isDragSessionActive &&
    (isToolbarRowVisibleBySelection || showCollectionContextToolbar);
  const parentOwnsDragInteraction =
    !isDndDisabled && isBlockActive && (isCardBackedParent || isGroupBlock);
  const areChildrenDndDisabled = Boolean(disabled || dndDisabled) || parentOwnsDragInteraction;
  const handleAddCard =
    isCollectionParent || isStaticDataWidget
      ? () => {
          const itemsKey = isStaticDataWidget ? 'staticItems' : 'items';
          const currentItems = isStaticDataWidget
            ? (dataWidgetProps?.staticItems ?? [])
            : resolveSymmetricCollectionProps(block).items;
          updateBlock(sectionId, block.id, {
            props: {
              ...(block.props ?? {}),
              [itemsKey]: [...currentItems, createEmptyCollectionItem(cardConfig.fields)],
            },
          });
        }
      : undefined;
  const openCollectionNode = isCollectionParent ? (
    <CollectionSettingsDialog
      sectionId={sectionId}
      block={block}
      cardTemplateBlock={cardTemplateBlock}
      cardTemplateChildren={cardTemplateChildren}
      isVisible
      disabled={disabled}
      showFloatOption={showFloatOption}
      onOpenChange={(open) => {
        if (open) setActiveBlock(editorSectionId, blockUiKey, { syncHover: true });
      }}
    />
  ) : undefined;
  const openDataWidgetNode = isDataWidgetParent ? (
    <DataWidgetSettingsDialog
      sectionId={sectionId}
      block={block}
      cardTemplateBlock={cardTemplateBlock}
      cardTemplateChildren={cardTemplateChildren}
      isVisible
      disabled={disabled}
      onOpenChange={(open) => {
        if (open) setActiveBlock(editorSectionId, blockUiKey, { syncHover: true });
      }}
    />
  ) : undefined;
  const openIndexCollectionNode = isIndexCollectionParent ? (
    <IndexCollectionSettingsDialog
      sectionId={sectionId}
      block={block}
      cardTemplateBlock={cardTemplateBlock}
      cardTemplateChildren={cardTemplateChildren}
      isVisible
      disabled={disabled}
      onOpenChange={(open) => {
        if (open) setActiveBlock(editorSectionId, blockUiKey, { syncHover: true });
      }}
    />
  ) : undefined;
  const openSettingsNode = showGroupSettings ? (
    <GroupSettingsPopover
      block={block}
      onUpdateGroupProps={onUpdateGroupProps}
      isVisible
      showLabel
      disabled={disabled}
      showFloatOption={showFloatOption}
      sectionId={sectionId}
    />
  ) : undefined;
  const handleDelete = useCallback(() => {
    if (isBlockActive) {
      if (parentStableId) {
        setActiveBlock(editorSectionId, parentStableId, { syncHover: true });
      } else {
        clearActiveForSection();
      }
    }

    deleteBlock(sectionId, block.id, {
      preserveEmptyParentIds: protectedBlockIds,
    });
  }, [
    block.id,
    clearActiveForSection,
    deleteBlock,
    editorSectionId,
    isBlockActive,
    parentStableId,
    protectedBlockIds,
    sectionId,
    setActiveBlock,
  ]);
  const handleSelectParent = useCallback(() => {
    if (!parentStableId) {
      return;
    }

    setActiveBlock(editorSectionId, parentStableId, { syncHover: true });
  }, [editorSectionId, parentStableId, setActiveBlock]);
  return (
    <div
      ref={blockRootRef}
      className={cn(
        'relative',
        layoutClassName,
        (stretchToParent || shouldStretchRootGroup) && 'h-full flex-1 flex flex-col',
      )}
      data-block-selection-root={blockUiKey}
      {...blockSelectionBindings}
    >
      {toolbarRowVisible ? (
        <FloatingBlockToolbarPortal anchorRef={blockRootRef}>
          <div
            className="pointer-events-auto flex items-center"
            onMouseEnter={isCardBackedParent ? cancelCollectionHoverHide : undefined}
            onMouseLeave={isCardBackedParent ? scheduleCollectionHoverHide : undefined}
          >
            <BlockActionToolbar
              block={block}
              disabled={disabled}
              onSelectParent={canSelectParent ? handleSelectParent : undefined}
              onAddCard={handleAddCard}
              onOpenCollection={openCollectionNode || openIndexCollectionNode || openDataWidgetNode}
              onOpenSettings={openSettingsNode}
              onDelete={
                isOutsideStructuralRegion || isProtectedBlock || isIndexCollectionParent
                  ? undefined
                  : handleDelete
              }
            />
          </div>
        </FloatingBlockToolbarPortal>
      ) : null}
      <BlockShell
        isParentLike={isParentLike}
        isSelected={isBlockActive}
        isHovered={
          isCardBackedParent
            ? (hoverChromeAllowed && (isHoverInSubtree || stickyHoverInSubtree)) ||
              collectionItemReorderDragActive
            : isBlockHovered
        }
        showHoverAsSelected={isCardBackedParent}
        isSelectionScope={showSelectionScope}
        isDropHover={isDropHover}
        isDragging={isDragging}
        className={cn(
          (stretchToParent || shouldStretchRootGroup) && 'h-full flex-1 flex flex-col',
          previewGapClass,
          isDragSessionActive && 'transition-[margin] duration-150 ease-out',
        )}
      >
        <div
          ref={mergeRefs(sortableRef as (node: HTMLElement | null) => void, setDropRef)}
          className={cn(
            'relative min-w-0 w-full',
            isDragging && 'opacity-70 scale-[0.8] transition-[opacity,transform] duration-150',
            (stretchToParent || shouldStretchRootGroup) && 'h-full flex-1 flex flex-col',
            isAllowedScope && DND_ACTIVE_SCOPE_CLASS,
          )}
        >
          <GapZone
            blockId={blockUiKey}
            parentId={parentStableId}
            parentDirection={parentDirection}
            disabled={!isDragSessionActive || isDragging || isDndDisabled || isInlineEditInSubtree}
          />
          {placementPreviewNode}

          {dropIndicator?.targetId === `parent-drop-${blockUiKey}` && (
            <div className={getParentDropSurfaceIndicatorClassName(childParentDirection)} />
          )}

          {isCollectionParent || isStaticDataWidget ? (
            <SortableCollectionItems
              sectionId={sectionId}
              editorSectionId={editorSectionId}
              block={block}
              cardConfig={cardConfig}
              disabled={disabled || isDragSessionActive || parentOwnsDragInteraction}
              actionsDisabled={disabled || isDragSessionActive}
              collectionBlockActive={isBlockActive}
              onCollectionItemReorderDragActiveChange={setCollectionItemReorderDragActive}
              itemsKey={isStaticDataWidget ? 'staticItems' : undefined}
            />
          ) : isCardBackedParent && !isCollectionParent && !isStaticDataWidget ? (
            <div className="group relative w-full">
              {isDataWidgetParent ? (
                <div className="pointer-events-none absolute left-2 top-2 z-10 opacity-0 transition-opacity group-hover:opacity-100">
                  <Badge
                    colorScheme="success"
                    variant="solid"
                    withDot
                    size="sm"
                    className="bg-white"
                  >
                    {`Connected to ${isDataWidgetBlockType(block.blockType) ? getDataWidgetLabel(block.blockType) : 'Live Data'}`}
                  </Badge>
                </div>
              ) : null}
              {isIndexCollectionParent ? (
                <IndexCollectionRenderer block={block} cardConfig={cardConfig} />
              ) : (
                <DataWidgetRenderer block={block} cardConfig={cardConfig} />
              )}
            </div>
          ) : (
            <GroupBackgroundShell
              layout={resolvedGroupLayout}
              blockProps={block.props ?? {}}
              siteTextColor={cardColors?.siteText}
              stretch={stretchToParent || shouldStretchRootGroup}
            >
              <div
                ref={groupContainerRef}
                className={cn(
                  getGroupContainerClassName(resolvedGroupLayout, { blockFlowForAncestorFloat }),
                  getGroupSurfaceClassName(resolvedGroupLayout),
                  getGroupCustomStyleClassName(resolvedGroupLayout),
                  !hasGroupMediaBackground(resolvedGroupLayout) &&
                    resolvedGroupLayout.backgroundColor &&
                    getCardBgClassName(
                      { backgroundColor: resolvedGroupLayout.backgroundColor },
                      cardColors,
                    ),
                  hasGroupMediaBackground(resolvedGroupLayout) && 'relative z-10',
                  (stretchToParent || shouldStretchRootGroup) && 'h-full flex-1',
                )}
                style={getGroupCustomStyle(resolvedGroupLayout)}
              >
                {children.length > 0 ? (
                  children.map((child, childIndex) => (
                    <div
                      key={getBlockUiKey(child)}
                      className={cn(
                        getChildWrapperClassName(child),
                        'transition-[transform,margin,opacity] duration-200 ease-out',
                      )}
                      style={getBlockDisplayWidthStyle(child)}
                    >
                      {isParentBlockType(child.blockType) ? (
                        <SortableParentBlock
                          sectionId={sectionId}
                          editorSectionId={editorSectionId}
                          block={child}
                          parentId={block.id}
                          parentStableId={blockUiKey}
                          parentBlockType={block.blockType}
                          parentDirection={childParentDirection}
                          childrenByParent={childrenByParent}
                          dropIndicator={dropIndicator}
                          onUpdateGroupProps={onUpdateGroupProps}
                          isDragSessionActive={isDragSessionActive}
                          allowedScopeParentId={allowedScopeParentId}
                          disabled={disabled}
                          dndDisabled={areChildrenDndDisabled}
                          stretchToParent={shouldStretchChildren}
                          hasActiveAncestor={hasActiveAncestor || isBlockActive}
                          structuralEditableBlockIds={structuralEditableBlockIds}
                          protectedBlockIds={protectedBlockIds}
                          structuralRegionByRootId={structuralRegionByRootId}
                          participatesInAncestorFloatFlow={
                            blockFlowForAncestorFloat ||
                            (firstFloatedChildIndex >= 0 && childIndex > firstFloatedChildIndex)
                          }
                        />
                      ) : (
                        <SortableLeafBlock
                          sectionId={sectionId}
                          editorSectionId={editorSectionId}
                          block={child}
                          parentStableId={blockUiKey}
                          parentBlockType={block.blockType}
                          parentDirection={childParentDirection}
                          contentAlign={childContentAlign}
                          dropIndicator={dropIndicator}
                          isDragSessionActive={isDragSessionActive}
                          disabled={disabled}
                          dndDisabled={areChildrenDndDisabled}
                          stretchToParent={shouldStretchChildren}
                          structuralEditableBlockIds={structuralEditableBlockIds}
                          protectedBlockIds={protectedBlockIds}
                        />
                      )}
                    </div>
                  ))
                ) : (
                  <div className={`${EMPTY_STATE_BASE} min-h-[120px] w-full`} />
                )}
                {structuralRegion && !isSelectionScope && !isInlineEditInSubtree ? (
                  <StructuralRegionAddElement
                    sectionId={sectionId}
                    parentBlockId={block.id}
                    structuralRegion={structuralRegion}
                    hasOptionalElements={children.length > 0}
                    isBlockActive={isBlockActive}
                    isHovered={isHoverInSubtree || stickyHoverInSubtree}
                    anchorRef={groupContainerRef}
                  />
                ) : null}
              </div>
            </GroupBackgroundShell>
          )}
        </div>
      </BlockShell>
    </div>
  );
}
